import { useLingui } from '@lingui/react/macro'
import { useRuntimeVariables } from 'app-kit'
import { useGeckoAuth } from 'features/auth'
import {
  DataPreviewResponse,
  ExperimentConfig,
  ExperimentRun,
  LoadExperimentResponse,
  NormalizeResponse,
  NormMethod,
  SavedExperimentSummary,
  SaveExperimentResponse,
  TrainProgressEvent,
  TrainResponse,
} from 'features/timeSeries/experiment/types'
import { useCallback, useRef, useState } from 'react'

const errorMessage = (error: unknown): string => {
  if (
    typeof error === 'object' &&
    error !== null &&
    'response' in error &&
    typeof error.response === 'object' &&
    error.response !== null &&
    'data' in error.response
  ) {
    const data = error.response.data
    if (typeof data === 'object' && data !== null && 'detail' in data) {
      return String(data.detail)
    }
  }
  return error instanceof Error ? error.message : String(error)
}

/** Build the JSON body shared by preview / train. */
export const configToPayload = (config: ExperimentConfig) => ({
  target: config.target,
  drivers: config.drivers,
  start_date: config.start_date || undefined,
  end_date: config.end_date || undefined,
  sampling_mode: config.sampling_mode,
  interval_minutes: config.interval_minutes,
  interval: config.interval ?? config.interval_minutes,
  interval_unit: config.interval_unit,
  aggregation_op: config.aggregation_op,
  gap_fill: config.gap_fill,
  cleaning_plan: config.cleaning_plan,
  gate_scope: config.gate_scope,
  phase_shifts: config.phase_shifts,
  corr_shift_max: config.corr_shift_max,
  corr_shift_step: config.corr_shift_step,
  feature: {
    ...config.feature,
    phase_shifts: config.phase_shifts,
  },
  split: config.split,
  y_ranges: config.y_ranges,
  residual_band_k: config.residual_band_k,
  band_estimator: config.band_estimator,
  band_quantile: config.band_quantile,
  knn_k: config.knn_k,
  knn_fit_cap: config.knn_fit_cap,
  fit_cap: config.fit_cap,
  score_complete_rows_only: config.score_complete_rows_only,
  min_usable_rows: config.min_usable_rows,
  min_train_rows: config.min_train_rows,
  mlp_hidden_layer_sizes: config.mlp_hidden_layer_sizes,
  mlp_max_iter: config.mlp_max_iter,
  random_state: config.random_state,
  methods: config.methods,
  notes: config.notes,
})

export interface SaveExperimentOptions {
  name: string
  config: ExperimentConfig
  result?: TrainResponse | null
  runs?: ExperimentRun[]
  notes?: string
  entryId?: string | null
  includeModels?: boolean
}

export const buildSaveExperimentPayload = (opts: SaveExperimentOptions) => ({
  name: opts.name,
  config: configToPayload(opts.config),
  result: opts.result || undefined,
  runs: opts.runs,
  notes: opts.notes || '',
  entry_id: opts.entryId || undefined,
  include_models: Boolean(opts.includeModels),
})

export type LiveLossCurves = Record<
  string,
  { train_loss: number[]; val_loss: number[] }
>

export const useExperimentApi = (siteId: string | null) => {
  const { t } = useLingui()

  const { FIM_SERVICE_URL } = useRuntimeVariables()
  const { authorizedAxios, authorization } = useGeckoAuth()
  const base = siteId
    ? `${FIM_SERVICE_URL}/api/v1/sites/${encodeURIComponent(siteId)}/sentinel/experiment`
    : null

  const [preview, setPreview] = useState<DataPreviewResponse | null>(null)
  const [previewLoading, setPreviewLoading] = useState(false)
  const [previewError, setPreviewError] = useState<string | null>(null)

  const [trainResult, setTrainResult] = useState<TrainResponse | null>(null)
  const [trainLoading, setTrainLoading] = useState(false)
  const [trainError, setTrainError] = useState<string | null>(null)
  const [trainProgress, setTrainProgress] = useState<TrainProgressEvent[]>([])
  const [liveLossCurves, setLiveLossCurves] = useState<LiveLossCurves>({})

  const [normalizeResult, setNormalizeResult] =
    useState<NormalizeResponse | null>(null)
  const [normalizeLoading, setNormalizeLoading] = useState(false)
  const [normalizeError, setNormalizeError] = useState<string | null>(null)

  const [savedList, setSavedList] = useState<SavedExperimentSummary[]>([])
  const [savedLoading, setSavedLoading] = useState(false)
  const [savedError, setSavedError] = useState<string | null>(null)
  const [saveLoading, setSaveLoading] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [loadLoading, setLoadLoading] = useState(false)

  const previewSeq = useRef(0)
  const trainAbort = useRef<AbortController | null>(null)

  const fetchPreview = useCallback(
    async (config: ExperimentConfig) => {
      if (!base || !config.target || !config.drivers.length) return null
      const seq = ++previewSeq.current
      // Stale-while-revalidate: keep prior preview visible while refetching.
      setPreviewLoading(true)
      setPreviewError(null)
      try {
        const res = await authorizedAxios.post<DataPreviewResponse>(
          `${base}/data-preview`,
          configToPayload(config)
        )
        if (seq !== previewSeq.current) return null
        setPreview(res.data)
        return res.data
      } catch (err) {
        if (seq !== previewSeq.current) return null
        const msg = errorMessage(err)
        setPreviewError(msg)
        // Leave previous preview intact on error.
        return null
      } finally {
        if (seq === previewSeq.current) setPreviewLoading(false)
      }
    },
    [authorizedAxios, base]
  )

  const train = useCallback(
    async (config: ExperimentConfig) => {
      if (!base) return null
      trainAbort.current?.abort()
      const ac = new AbortController()
      trainAbort.current = ac

      setTrainLoading(true)
      setTrainError(null)
      setTrainProgress([])
      setLiveLossCurves({})
      setTrainResult(null)

      const pushProgress = (ev: TrainProgressEvent) => {
        setTrainProgress((prev) => [...prev.slice(-80), ev])
        if (
          ev.type === 'mlp_epoch' &&
          ev.method &&
          typeof ev.train_loss === 'number'
        ) {
          setLiveLossCurves((prev) => {
            const cur = prev[ev.method!] || { train_loss: [], val_loss: [] }
            return {
              ...prev,
              [ev.method!]: {
                train_loss: [...cur.train_loss, ev.train_loss!],
                val_loss:
                  typeof ev.val_loss === 'number'
                    ? [...cur.val_loss, ev.val_loss]
                    : cur.val_loss,
              },
            }
          })
        }
        if (ev.type === 'method_done' && ev.method && ev.loss_curve) {
          setLiveLossCurves((prev) => ({
            ...prev,
            [ev.method!]: {
              train_loss:
                ev.loss_curve?.train_loss || prev[ev.method!]?.train_loss || [],
              val_loss:
                ev.loss_curve?.val_loss ||
                ev.loss_curve?.validation_loss_proxy ||
                prev[ev.method!]?.val_loss ||
                [],
            },
          }))
        }
      }

      try {
        const res = await fetch(`${base}/train-stream`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'text/event-stream',
            ...(authorization ? { Authorization: authorization } : {}),
          },
          body: JSON.stringify(configToPayload(config)),
          signal: ac.signal,
        })
        if (!res.ok) {
          const text = await res.text()
          throw new Error(text || `train-stream HTTP ${res.status}`)
        }
        const reader = res.body?.getReader()
        if (!reader) throw new Error('No response body for train-stream')

        const decoder = new TextDecoder()
        let buffer = ''
        let finalResult: TrainResponse | null = null

        while (true) {
          const { done, value } = await reader.read()
          if (done) break
          buffer += decoder.decode(value, { stream: true })
          const chunks = buffer.split('\n\n')
          buffer = chunks.pop() || ''
          for (const chunk of chunks) {
            const line = chunk.split('\n').find((l) => l.startsWith('data: '))
            if (!line) continue
            let ev: TrainProgressEvent
            try {
              ev = JSON.parse(line.slice(6)) as TrainProgressEvent
            } catch {
              continue
            }
            if (ev.event === 'result' && ev.data) {
              finalResult = ev.data
              if (!ev.data.ok) {
                setTrainError(ev.data.error || t`train failed`)
              } else {
                setTrainResult(ev.data)
              }
            } else if (ev.event === 'error') {
              setTrainError(ev.error || ev.message || t`train failed`)
            } else {
              pushProgress(ev)
            }
          }
        }

        return finalResult
      } catch (err) {
        if ((err as Error).name === 'AbortError') return null
        const msg = errorMessage(err)
        setTrainError(msg)
        return null
      } finally {
        setTrainLoading(false)
      }
    },
    [authorization, base, t]
  )

  const cancelTrain = useCallback(() => {
    trainAbort.current?.abort()
  }, [])

  const normalize = useCallback(
    async (config: ExperimentConfig, against: string[], method: NormMethod) => {
      if (!base || !config.target || !against.length) return null
      setNormalizeLoading(true)
      setNormalizeError(null)
      try {
        const res = await authorizedAxios.post<NormalizeResponse>(
          `${base}/normalize`,
          {
            target: config.target,
            against,
            method,
            start_date: config.start_date || undefined,
            end_date: config.end_date || undefined,
            interval_minutes: config.interval_minutes,
            aggregation_op: config.aggregation_op,
            gap_fill: config.gap_fill,
            phase_shifts: config.phase_shifts,
          }
        )
        setNormalizeResult(res.data)
        return res.data
      } catch (err) {
        const msg = errorMessage(err)
        setNormalizeError(msg)
        return null
      } finally {
        setNormalizeLoading(false)
      }
    },
    [authorizedAxios, base]
  )

  const listSaved = useCallback(async () => {
    if (!base) return []
    setSavedLoading(true)
    setSavedError(null)
    try {
      const res = await authorizedAxios.get<{
        ok: boolean
        items: SavedExperimentSummary[]
      }>(`${base}/saved`)
      const items = res.data.items || []
      setSavedList(items)
      return items
    } catch (err) {
      const msg = errorMessage(err)
      setSavedError(msg)
      return []
    } finally {
      setSavedLoading(false)
    }
  }, [authorizedAxios, base])

  const saveExperiment = useCallback(
    async (opts: SaveExperimentOptions) => {
      if (!base) return null
      setSaveLoading(true)
      setSaveError(null)
      try {
        const res = await authorizedAxios.post<SaveExperimentResponse>(
          `${base}/saved`,
          buildSaveExperimentPayload(opts)
        )
        await listSaved()
        return res.data
      } catch (err) {
        const msg = errorMessage(err)
        setSaveError(msg)
        return null
      } finally {
        setSaveLoading(false)
      }
    },
    [authorizedAxios, base, listSaved]
  )

  const loadExperiment = useCallback(
    async (entryId: string) => {
      if (!base) return null
      setLoadLoading(true)
      setSavedError(null)
      try {
        const res = await authorizedAxios.get<LoadExperimentResponse>(
          `${base}/saved/${encodeURIComponent(entryId)}`
        )
        return res.data
      } catch (err) {
        const msg = errorMessage(err)
        setSavedError(msg)
        return null
      } finally {
        setLoadLoading(false)
      }
    },
    [authorizedAxios, base]
  )

  const deleteExperiment = useCallback(
    async (entryId: string) => {
      if (!base) return false
      setSavedError(null)
      try {
        await authorizedAxios.delete(
          `${base}/saved/${encodeURIComponent(entryId)}`
        )
        setSavedList((prev) => prev.filter((x) => x.entry_id !== entryId))
        return true
      } catch (err) {
        const msg = errorMessage(err)
        setSavedError(msg)
        return false
      }
    },
    [authorizedAxios, base]
  )

  return {
    preview,
    previewLoading,
    previewError,
    fetchPreview,
    trainResult,
    setTrainResult,
    trainLoading,
    trainError,
    trainProgress,
    liveLossCurves,
    train,
    cancelTrain,
    normalizeResult,
    normalizeLoading,
    normalizeError,
    normalize,
    savedList,
    savedLoading,
    savedError,
    saveLoading,
    saveError,
    loadLoading,
    listSaved,
    saveExperiment,
    loadExperiment,
    deleteExperiment,
  }
}
